import { useEffect } from 'react';
import { twJoin } from 'tailwind-merge';

import { AnswersMarkdown, SearchQueryContext } from './AnswersMarkdown';
import {
  AnswerBody,
  AnswersHeading,
  QuestionHeader,
  SpecialistHeader,
} from './InquiryLibraryAnswersParts';
import { AnswersPostIdContext } from './answersAnalytics';
import { AnswersFoldsContext, useAnswersFolds, useAnswersFoldsState } from './answersFolds';
import {
  ANSWERS_CARD_CLASS,
  ANSWERS_STRIP_CLASS,
  ANSWERS_STRIP_RULE_CLASS,
  answerAnchor,
  answerBodyId,
  answerKey,
  specialistName,
} from './answersLayout';
import { type InquiryLibraryAnswers, postFormatOf } from '../../model/answers';
import type { LibraryPostAnswer } from '../../model/types';

import { VStack } from '@/components/ui/vstack';
import { trackInquiryLibraryAnswersViewed } from '@/util/analyticsEvents';

/**
 * The question as a card with the same structure as an answer card: a gray header strip with the
 * submitter's avatar and the label, then a white body with the case folded past about four lines
 * behind Show more, and the closing ask kept in view. No tint: the avatar and the label mark it as
 * the question.
 *
 * The strip is not blue on purpose: secondary fills mark Primum or AI content, and `secondary-50`
 * belongs to the summary card.
 */
function QuestionCard({ question }: { question: string }) {
  return (
    <VStack role="group" aria-label="Submitter's question" className={ANSWERS_CARD_CLASS}>
      <VStack className={twJoin(ANSWERS_STRIP_CLASS, ANSWERS_STRIP_RULE_CLASS)}>
        <QuestionHeader />
      </VStack>

      <VStack className="p-4">
        <AnswersMarkdown text={question} />
      </VStack>
    </VStack>
  );
}

/**
 * One Primum Specialist's answer as a white card on the `outline-200` border. The header strip is
 * the same gray strip as the question's and holds the specialist's avatar with its number badge,
 * the name as a heading, the answer's time and the chevron. Role shows in the avatar and the name,
 * not in the strip. Below it is a white body: the first reply in full, then every follow-up
 * continuing in the same card behind a line.
 */
function AnswerCard({ answer }: { answer: LibraryPostAnswer }) {
  const folds = useAnswersFolds();

  const collapsed = folds.isAnswerCollapsed(answerKey(answer.id));

  return (
    <VStack
      nativeID={answerAnchor(answer.position)}
      role="group"
      aria-label={`Answer from ${specialistName(answer.position)}`}
      className={ANSWERS_CARD_CLASS}>
      <VStack className={twJoin(ANSWERS_STRIP_CLASS, !collapsed && ANSWERS_STRIP_RULE_CLASS)}>
        <SpecialistHeader answer={answer} />
      </VStack>

      {collapsed ? null : (
        <VStack nativeID={answerBodyId(answer.position)}>
          <AnswerBody answer={answer} />
        </VStack>
      )}
    </VStack>
  );
}

/**
 * An Inquiry Library post as a question and a card per Primum Specialist answer: the question
 * once, then the answers section: the heading row,
 * the count on the left and Collapse all on the right, then the answers in
 * `position` order. The summary card is not here: it sits above the whole body, see
 * `CommunityPostContent`. There are no votes, and reader comments are not here; they stay in the
 * comments section below the post.
 *
 * Replaces the post's body rather than sitting beside it, so the same words never show twice. It
 * carries no names, ids or profile lines: the specialist is a position. A time shows only when the
 * API sent one, beside its author's name. A specialist's avatar is keyed to its number, the same
 * in its card header and its replies, and never to a person.
 */
export function InquiryLibraryAnswersBody({
  post,
  postId = null,
  searchQuery = '',
}: {
  post: InquiryLibraryAnswers;
  /** The post's id, for analytics. Without it the view sends no events. */
  postId?: number | null;
  /** The search the post was opened from, whose words are marked in the text. */
  searchQuery?: string;
}) {
  const folds = useAnswersFoldsState(post.answers.map((answer) => answerKey(answer.id)));

  // Once per post on screen: the count of readers who have seen the answers format.
  useEffect(() => {
    if (postId !== null) {
      trackInquiryLibraryAnswersViewed({ postId, format: postFormatOf(post) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one impression per post, not per render
  }, [postId]);

  return (
    <AnswersFoldsContext.Provider value={folds}>
      <AnswersPostIdContext.Provider value={postId}>
        <SearchQueryContext.Provider value={searchQuery}>
          <VStack className="gap-6">
            <QuestionCard question={post.question} />

            <VStack className="gap-2">
              <AnswersHeading count={post.answers.length} />

              <VStack className="gap-4">
                {post.answers.map((answer) => (
                  <AnswerCard key={answer.id} answer={answer} />
                ))}
              </VStack>
            </VStack>
          </VStack>
        </SearchQueryContext.Provider>
      </AnswersPostIdContext.Provider>
    </AnswersFoldsContext.Provider>
  );
}
